<template>
  <div id="backorders">
    <template v-if="activeView === 'REQUEST_FOLLOW_UP'">
      <follow-up :backorder="selectedRow" :show-grid="showGrid" />
    </template>
    <template v-else-if="activeView === 'ALTERNATIVE_PARTS'">
      <div class="container">
        <alternative-parts
          :part-alternatives="selectedPartAlternatives"
          :show-grid="showGrid"
          :oem-part-code="selectedOemPartCodeForAlternatives"
          :busy="loadingPartAlternative"
        />
      </div>
    </template>
    <list-layout v-show="activeView === 'GRID'" id="backorders">
      <template #leftSidebar>
        <orders-menu />
        <br />
        <orders-search-widget-form :dealerFilter="dealerFilters" />
      </template>

      <template #heading>
        <h1>{{ getContentItem("backorders") }}</h1>
      </template>

      <template #subTitle>
        <div class="backorders__subtitle" v-html="getContentItem('backordersSubTitle')" />
      </template>

      <template #filters>
        <div class="backorder-filters">
          <div class="backorder-filters__dropdown-wrapper">
            <available-dealer-code-selector
              v-if="user.DealerRange === '*'"
              :show-all-branches-option="false"
              class="controls backorder-filters__dealercodes"
              @selectDealerCode="setDealerCodes"
            />
            <available-dealer-code-selector
              v-if="user.DealerRange !== '*'"
              :show-all-branches-option="true"
              class="controls backorder-filters__dealercodes"
              @selectDealerCode="setDealerCodes"
            />
            <v-select
              v-if="IsMcfa"
              v-model="selectedOrderTypeInput"
              class="filter disableClear backorder-filters__ordertypes"
              :options="orderTypeOptions"
              @change="refreshData()"
            />
          </div>
        </div>
        <div class="backorders__filters">
          <h4>Filters</h4>
          <div>
            <div>
              <input class="backorders__filters-input" v-model="referenceNumberFilter" placeholder="Order or PO #" />
              <input class="backorders__filters-input" v-model="partCodeFilter" placeholder="Part #" />
              <button class="backorders__filters-btn" @click="refreshData()">Search</button>
            </div>
          </div>
        </div>
        <b-row class="backorders__filter-row">
          <b-col md="4">
            <div class="mb-2">
              <b-form-select
                id="per-page"
                v-model="pagination.perPage"
                :aria-label="getContentItem('perPage')"
                :options="pagination.perPageList"
                size="lg"
                class="form-control backorders__per-page"
                @change="refreshData()"
              />
            </div>
          </b-col>
          <b-col md="4">
            <div class="mb-2">
              <button
                class="backorders__download-button"
                :class="{
                  'backorders__download-button--disabled': filteredBackorders.length <= 0 || loadingBackorders,
                }"
                :disabled="filteredBackorders.length <= 0 || loadingBackorders"
              >
                <download-json-to-csv
                  class="backorders__download-json"
                  :data="backordersForDownload"
                  file-name="Backorders.csv"
                  :disabled="filteredBackorders.length <= 0 || loadingBackorders"
                >
                  Download Backorders CSV
                </download-json-to-csv>
              </button>
            </div>
          </b-col>
        </b-row>
        <b-row v-if="user.DealerRange === '*'">
          <b-col>
            <div class="alert alert-secondary">
              {{ getContentItem("backordersStarUserMessage") }}
            </div>
          </b-col>
        </b-row>
        <b-row v-if="highlightedRowPresent">
          <b-col>
            <div class="alert alert-secondary bo__modified-message">
              <fa-icon class="bo__table-modified-icon bo__modified-message-icon fa-2x" icon="info-circle" />
              <span>{{ getContentItem("backordersEstimateDateMessage") }}</span>
            </div>
          </b-col>
        </b-row>
      </template>
      <template #table>
        <div>
          <DnTable
            id="backordersTable"
            ref="backordersTableRef"
            class="blue backorders_table"
            :fields="columns"
            :items="backorders"
            sortBy="ModifiedDate"
            :sortDescProp="true"
            :currentPage="pagination.currentPage"
            :perPage="pagination.perPage"
            :busy="loadingBackorders"
            :showPagination="true"
            :usePagination="false"
            :emptyText="'No Backorders Found'"
            :totalRows="pagination.totalRows"
            @sort-changed="paginationReset"
            @update:currentPage="handlePageChange"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <h2>
                  <loading-spinner class="mx-auto" />
                </h2>
              </div>
            </template>
            <template #RecentlyModified="{ row }">
              <fa-icon
                v-if="showRecentlyModified(row?.item)"
                class="bo__table-modified-icon fa-2x"
                icon="info-circle"
              />
            </template>
            <template #OemPartCode="{ row }">
              <div>
                <part-details-link class="nowrap" :oem-part-code="row.item.OemPartCode" />
              </div>
              <div>
                <p class="nowrap m-0">
                  {{ row.item.PartDescription }}
                </p>
              </div>
              <div>
                <button
                  v-if="row.item.AlternativeParts && row.item.AlternativeParts.length > 0"
                  class="btn btn-default nowrap backorder-alternative-button"
                  @click="showAlternativeParts(row.item.OemPartCode, row.item.AlternativeParts)"
                >
                  Alternative Parts ({{ row.item.AlternativeParts.length }})
                </button>
              </div>
            </template>
            <template #PreviousEstimatedShipDate="{ row }">
              {{ previousShipDate(row.item.PreviousEstimatedShipDate) }}
            </template>
            <template #EstimatedVendorShipDate="{ row }">
              <template
                v-if="Date.parse(row.item.EstimatedVendorShipDate) < Date.parse(new Date().toLocaleDateString('en-US'))"
              >
                Inbound Past Due
              </template>
              <template v-else>
                {{ formatDateTime(row.item.EstimatedVendorShipDate, "Call Us") }}
              </template>
            </template>
            <template #OrderCreatedDate="{ row }">
              {{ formatDateTime(row.item.OrderCreatedDate) }}
            </template>
            <template #ModifiedDate="{ row }">
              {{ formatDateTime(row.item.ModifiedDate) }}
            </template>
            <template #OrderNumber="{ row }">
              <order-details-link
                :order-number="row.item.OrderNumber"
                :order-source="row.item.BackorderType"
                :dealer-code="row.item.DealerCode"
                :sales-organization="row.item.SalesOrganization"
              />
            </template>
            <template #options="{ row }">
              <backorder-options
                :key="`backorder-options-${row.item.Id}`"
                :backorder="row.item"
                :show-details="row.detailsShowing"
                @showAlternativeParts="showAlternativeParts"
                @toggleDetails="toggleDetails"
                @requestFollowup="requestFollowUp"
              />
            </template>
            <template #row-details="{ row }">
              <backorder-row-details :backorder="row.item" />
            </template>
          </DnTable>
          <b-row v-if="pagination.totalRows > 0">
            <b-col id="paginationDisplay" md="6" class="pull-left">
              {{
                format(
                  getContentItem("showingPagination"),
                  paginationFirstRecord,
                  paginationLastRecord,
                  pagination.totalRows
                )
              }}
            </b-col>
          </b-row>
        </div>
      </template>
    </list-layout>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import vSelect from "vue-select";
import { BackorderService, ErrorLogService, GridUtil } from "@/services/";
import {
  ILoadAllBackOrdersForUserRequestDto,
  IBackOrdersAlternativesListRequestDto,
} from "@/services/BackorderService";
import {
  ListLayout,
  LoadingSpinner,
  OrderDetailsLink,
  OrdersMenu,
  OrdersSearchWidgetForm,
  PartDetailsLink,
} from "@/components";
import { formatDateTime, paginationMixin, stringHelper } from "@/components/mixins";
import { default as AlternativeParts } from "../AlternativeParts/AlternativeParts.vue";
import { default as BackorderOptions } from "../BackorderOptions/BackorderOptions.vue";
import { default as BackorderRowDetails } from "../BackorderRowDetails/BackorderRowDetails.vue";
import { default as FollowUp } from "../Details/FollowUp.vue";
import AvailableDealerCodeSelector from "@/components/Dropdowns/AvailableDealerCodeSelector";
import { DownloadJsonToCsv } from "@/components/Elements/DownloadJsonToCsv";
import moment from "moment";
import { DealerUtil } from "@/services/";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  name: "Backorders",
  components: {
    ListLayout,
    LoadingSpinner,
    OrdersMenu,
    OrdersSearchWidgetForm,
    OrderDetailsLink,
    PartDetailsLink,
    AlternativeParts,
    FollowUp,
    BackorderOptions,
    AvailableDealerCodeSelector,
    vSelect,
    BackorderRowDetails,
    DownloadJsonToCsv,
    DnTable,
  },
  mixins: [formatDateTime, paginationMixin, stringHelper],
  data: () => ({
    columns: [
      { key: "RecentlyModified", label: "" },
      { key: "OrderNumber", label: "Order #", sortable: true },
      { key: "PurchaseOrderNumber", label: "PO #", sortable: true },
      { key: "OemPartCode", label: "Part #", sortable: true },
      { key: "DealerCode", label: "Dealer", sortable: true },
      { key: "PreviousEstimatedShipDate", label: "Previous Estimated Date", sortable: true },
      { key: "EstimatedVendorShipDate", label: "Estimated Date", sortable: true },
      { key: "TotalQuantityOrdered", label: "Qty", sortable: true },
      { key: "TotalQuantityBackordered", label: "Qty Backordered", sortable: true },
      { key: "OrderType", label: "Order Type", sortable: true },
      { key: "OrderCreatedDate", label: "Order Date", sortable: true },
      { key: "ModifiedDate", label: "Modified Date", sortable: true },
      { key: "options", label: "Options", sortable: false },
    ],
    loadingBackorders: false,
    backorders: [],
    selectedOrderTypeInput: { label: "Warranty & Emergency", code: "Warranty,Emergency" },
    textFilter: null,
    selectedRow: null,
    selectedPartAlternatives: null,
    selectedOemPartCodeForAlternatives: "",
    activeView: "GRID",
    modifiedDayRange: 3,
    orderTypeOptions: [
      { label: "Warranty & Emergency", code: "Warranty,Emergency" },
      { label: "All Order Types", code: "" },
      { label: "Stock", code: "Stock" },
    ],
    useAllDealerCodes: true,
    selectedDealerCodes: [],
    displayedFilteredBackorders: [],
    selectedAccountId: null,
    highlightedRowPresent: false,
    referenceNumberFilter: null,
    partCodeFilter: null,
    loadingPartAlternative: false,
    dealerFilters: [],
  }),
  computed: {
    ...mapGetters([
      "user",
      "getContentItem",
      "hasPermission",
      "activeAccountId",
      "canSeeAnyPrice",
      "canSeeContractPrice",
      "canSeeDealerNet",
      "tenantDetails",
      "audienceIds",
      "availableDealerCodes",
      "IsMcfa"
    ]),
    hasAccessToAllBranches() {
      return this.availableDealerCodes.length > 1 && this.user.DealerRange !== "*";
    },
    filteredBackorders() {
      return this.backorders;
    },
    backordersForDownload() {
      var self = this;
      // 72 hours before now; rows modified after this are flagged in the CSV
      var cutoff = new Date(Date.now() - 72 * 60 * 60 * 1000);
      // Is the modified date within the last 72 hours?
      var isRecentlyModified = (x) => new Date(x.ModifiedDate) > cutoff;
      // Check if any backorders have been modified recently
      var anyRecentlyModified = this.backorders.some(isRecentlyModified);
      var backordersForDownload = this.backorders.map(function (x) {
        var row = {
          OrderNumber: x.OrderNumber,
          PoNumber: x.PurchaseOrderNumber,
          PartNumber: x.OemPartCode,
          PartDescription: x.PartDescription,
          DealerCode: x.DealerCode,
          EstimatedDate: self.formatDateTime(x.EstimatedVendorShipDate, "Call Us"),
          Quantity: x.TotalQuantityOrdered,
          QuantityBackordered: x.TotalQuantityBackordered,
          OrderType: x.OrderType,
          OrderDate: self.formatDateTime(x.OrderCreatedDate),
          ModifiedDate: self.formatDateTime(x.ModifiedDate),
        };
        // Add a flag for recently modified rows if any exist and this row was modified within the last 72 hours
        if (anyRecentlyModified) {
          row.ModifiedLast72Hours = isRecentlyModified(x) ? "Yes" : "No";
        }
        return row;
      });
      return backordersForDownload;
    },
    dealerFilter() {
      if (this.currentDealerSelection.hasOwnProperty("value") && this.currentDealerSelection.value === "All Branches") {
        return this.allPrimaryDealerCodes;
      } else {
        return this.currentDealerSelection.DealerCodes.replace(/\s/g, "").split(",");
      }
    },
  },
  watch: {
    paginationPerPage() {
      this.pagination.currentPage = 1;
    },
    textFilter() {
      this.pagination.currentPage = 1;
    },
    backorders() {
      this.getPartAlternatives();
    },
  },
  mounted() {
    this.columns = GridUtil.localizeColumnLabels(this.columns, this.getContentItem);
    this.selectedAccountId = this.activeAccountId;

    // availableDealerCodes is a bit of a misnomer as each element has more data than just a DealerCode
    this.allPrimaryDealerCodes = this.availableDealerCodes.map((dealer) => dealer.PrimaryDealerCode);

    // Match's web/src/components/Dropdowns/AvailableDealerCodeSelector.vue setup
    this.currentDealerSelection = DealerUtil.determineCurrentDealerSelection(
      this.availableDealerCodes,
      this.user,
      this.hasAccessToAllBranches
    );

    this.dealerFilters = this.dealerFilter;
    this.backordersProvider();
  },
  created() {},
  methods: {
    async backordersProvider() {
      try {
        this.loadingBackorders = true;
        this.backorders = [];
        var self = this;
        let request = {
          AccountId: this.selectedAccountId,
          UserId: this.user.UserId,
          UserAudiences: this.audienceIds,
          PageNumber: this.pagination.currentPage,
          PageSize: this.pagination.perPage,
          ReferenceNumber: this.referenceNumberFilter,
          OemPartCode: this.partCodeFilter,
          OrderType: this.selectedOrderTypeInput.code,
          FuzzySearch:
            this.IsNotNullOrUndefined(this.referenceNumberFilter) || this.IsNotNullOrUndefined(this.partCodeFilter),
          DealerCodes: this.selectedDealerCodes,
        };
        const response = await BackorderService.getBackorders(request);
        this.backorders = response.Items;
        this.pagination.totalRows = response.Total;
      } catch (err) {
        ErrorLogService.LogException(err);
      } finally {
        this.loadingBackorders = false;
      }
    },
    handlePageChange(newPage) {
      this.pagination.currentPage = newPage;
      this.backordersProvider(); // Fetch new data for the selected page
    },
    async setDealerCodes(dealerCodesSelection) {
      this.pagination.currentPage = 1;
      this.backorders = [];
      let matchingDealer;
      let dealersCalled = new Array();
      var dealerCodes = [];
      if (dealerCodesSelection.value === "All Branches") {
        this.selectedDealerCodes = this.availableDealerCodes.map((x) => x.PrimaryDealerCode);
        this.selectedAccountId = null;
      } else {
        this.selectedDealerCodes = [];
        //trim all white space and then split into an array using comma as the delimiter
        dealerCodes = dealerCodesSelection.DealerCodes.replace(/\s/g, "").split(",");
        for (let i = 0; i < dealerCodes.length; i++) {
          matchingDealer = this.availableDealerCodes.find((x) => x.DealerCodes.includes(dealerCodes[i]));
          if (matchingDealer !== undefined && !dealersCalled.includes(matchingDealer.AccountId)) {
            dealersCalled.push(matchingDealer.AccountId);
            this.selectedAccountId = matchingDealer.AccountId;
          }
        }
      }
      this.refreshData();
    },

    clickAddCrmTicketNumber(backorder) {
      backorder.ShowAddEditCrmTicketNumberField = true;
    },
    refreshData() {
      this.backordersProvider()
    },
    filterGrid(row) {
      return GridUtil.filterGrid(row, this.textFilter);
    },
    showAlternativeParts(selectOemPartCodeForAlternatives, selectedPartAlternatives) {
      this.activeView = "ALTERNATIVE_PARTS";
      this.selectedOemPartCodeForAlternatives = selectOemPartCodeForAlternatives;
      this.getPartAlternatives(selectOemPartCodeForAlternatives);
    },
    getPartAlternatives(oemPartCode) {
      this.loadingPartAlternative = true;
      const context = {
        canSeeContractPrice: this.canSeeContractPrice,
        canSeeDealerNet: this.canSeeDealerNet,
        siteName: this.tenantDetails.McfCostCenter,
      };

      const data = {
        AccountId: this.activeAccountId,
        UserId: this.user.UserId,
        OemPartCode: oemPartCode,
        UserAudiences: this.audienceIds,
      };

      BackorderService.getPartAlternatives(context, data).then((result) => {
        this.selectedPartAlternatives = result;
        this.loadingPartAlternative = false;
        // don't jump out of for loop as we can only match on OemPartCode and there could be multiple backorders with same OemPartCode
        for (var dfCount = 0; dfCount < this.displayedFilteredBackorders.length; dfCount++) {
          var dfBackorder = this.displayedFilteredBackorders[dfCount];
          // update displayed data Parts Alternatives when the data comes back from the ajax call
          if (dfBackorder.OemPartCode == data.OemPartCode) {
            dfBackorder.partAlternatives = result;
            dfBackorder.AlternativesCount = result.length;
          }
        }
      });
    },
    requestFollowUp(row) {
      this.activeView = "REQUEST_FOLLOW_UP";
      this.selectedRow = row;
    },

    showGrid() {
      this.activeView = "GRID";
      this.selectedRow = null;
    },
    toggleDetails(backorder) {
      var backorderToToggle = this.filteredBackorders.filter(
        (x) =>
          (backorder.RpiGuid && x.RpiGuid === backorder.RpiGuid) ||
          (backorder.McfSalesOrderId && x.McfSalesOrderId === backorder.McfSalesOrderId)
      )[0];
      backorderToToggle._showDetails = !backorderToToggle._showDetails;
    },
    showRecentlyModified(item) {
      var dateString = item.ModifiedDate;
      var modifiedDate = new Date(dateString);
      var estimatedShipDate = new Date(item.EstimatedVendorShipDate);
      //Add n days to modified date
      modifiedDate.setDate(modifiedDate.getDate() + this.modifiedDayRange);
      //Return true if greater than current date
      var estimatedDateModified =
        item.PreviousEstimatedShipDate !== null && item.PreviousEstimatedShipDate != item.EstimatedVendorShipDate;
      var modifiedRecently = modifiedDate > new Date() && estimatedDateModified && estimatedShipDate > new Date();
      if (modifiedRecently) {
        this.highlightedRowPresent = true;
      }
      return modifiedRecently;
    },
    rowClass(item, type) {
      if (!item) {
        return;
      }
      if (this.showRecentlyModified(item)) {
        return "bo__table-modified-row";
      }
    },
    previousShipDate(date) {
      var prevDate = new Date(date);
      //If previous estimated ship date exists then show it.
      if (prevDate > new Date("1/1/1")) {
        return this.formatDateTime(date);
      } else {
        return null;
      }
    },
  },
};
</script>

<style lang="scss" src="./Backorders.scss"></style>
